"use client"

import { Suspense, type ComponentProps } from "react"
import clsx from "clsx"

import type { InferReturn } from "~/@types/generics"
import type { CollectionProductsPageInfo } from "~/lib/shopify/queries/get-sf-collection"
import { useShopifyCollectionProducts, type ShopifySearchFiltersData } from "~/hooks/useShopifyCollectionProducts"
import type { fetchSbCollection } from "~/components/ui/Collection/_data/get"
import type { serializeCollectionDefault } from "~/components/ui/Collection/CollectionDefault/_data/serializer"
import { CollectionNavigation } from "~/components/ui/Collection/CollectionDefault/components/CollectionNavigation"
import { HeaderControlsContainer } from "~/components/ui/Collection/CollectionDefault/components/HeaderControlsContainer"
import { PanelFiltersTrigger } from "~/components/ui/Collection/CollectionDefault/components/PanelFiltersTrigger"
import ProductGrid from "~/components/ui/Collection/CollectionDefault/components/ProductGrid"
import { ProductGridControls } from "~/components/ui/Collection/CollectionDefault/components/ProductGridControls"
import RoundedCta from "~/components/ui/RoundedCta"
import { ClientTranslate } from "~/providers/I18nProvider/ClientTranslate"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import { useSearchAndDiscoveryContext } from "~/providers/SearchAndDiscoveryProvider"

import { showFrom } from "~/styles/utils/show-hide"
import { text } from "~/styles/variants"

import * as css from "./styles.css"

export type CollectionDefaultProps = Pick<ShopifySearchFiltersData, "facets" | "results"> & {
  collection: InferReturn<typeof serializeCollectionDefault>
  sbCollection: InferReturn<typeof fetchSbCollection>["sbCollection"]
  pageInfo: CollectionProductsPageInfo
  withPagination: boolean
  locale: string
  isLoading?: boolean
  isSuspensed?: boolean
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  fetchNextPage?: () => void
  currentPage: number
}

function CollectionDefault({
  collection,
  facets,
  withPagination,
  fetchNextPage,
  hasNextPage = false,
  isLoading = false,
  isFetchingNextPage = false,
  results,
}: CollectionDefaultProps) {
  const t = useTranslate()

  return (
    <div className={css.CollectionDefault} data-comp="UI/Collection/CollectionDefault">
      <header className={css.CollectionDefaultHeader}>
        <div className={css.CollectionHeaderEdito}>
          <ClientTranslate
            as="p"
            tKey="collection_default_suptitle"
            className={clsx(
              showFrom("tablet", "block"),
              text({
                design: "riposte-13",
                color: "blue-deep",
                lineHeight: 1.45,
                letterSpacing: 1,
                transform: "uppercase",
                weight: "medium",
              })
            )}
          />
          <h1
            className={clsx(
              text({ design: "spezia-24-48", weight: "semiBold", transform: "uppercase", lineHeight: 0.9 })
            )}
          >
            {collection.title}
          </h1>
        </div>
        {collection.navigation.length > 0 && (
          <CollectionNavigation className={showFrom("tablet", "block")} navigation={collection.navigation} />
        )}
      </header>

      <HeaderControlsContainer navigation={collection.navigation}>
        <PanelFiltersTrigger facets={facets} />
        <ProductGridControls />
      </HeaderControlsContainer>

      <ProductGrid isLoading={!isFetchingNextPage && isLoading} products={results} />

      <Suspense>
        {withPagination && fetchNextPage && hasNextPage && (
          <div className={css.loadMoreWrapper}>
            <RoundedCta
              isLoading={isLoading}
              onClick={() => fetchNextPage()}
              theme="blue-deep"
              data-abtest="PLP/CTA/LoadMore"
            >
              {t("general_load_more")}
            </RoundedCta>
          </div>
        )}
      </Suspense>
    </div>
  )
}

export function DynamicCollectionDefault({
  collection,
  pageInfo,
  sbCollection,
  currentPage,
  ...props
}: Omit<ComponentProps<typeof CollectionDefault>, "facets" | "results">) {
  const { facets: initialFacets } = useSearchAndDiscoveryContext()

  const {
    data: collectionData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
  } = useShopifyCollectionProducts({
    id: collection.id,
    sbCollection,
    initialData: {
      currentPage,
      facets: initialFacets,
      results: collection?.products?.filter(Boolean) ?? [],
      pageInfo,
    },
  })

  const facets = collectionData?.pages?.[0]?.facets ?? initialFacets
  const results = collectionData?.pages.flatMap((page) => page.results) ?? collection?.products?.filter(Boolean) ?? []
  const computedPageInfo = collectionData?.pages?.[0]?.pageInfo ?? pageInfo

  return (
    <CollectionDefault
      isLoading={isLoading || isFetching}
      isFetchingNextPage={isFetchingNextPage}
      {...{
        collection,
        sbCollection,
        facets,
        currentPage,
        ...props,
        fetchNextPage,
        hasNextPage: hasNextPage ?? false,
        pageInfo: computedPageInfo,
        withPagination: computedPageInfo?.hasNextPage ?? false,
        results,
      }}
    />
  )
}

export function SuspensedCollectionDefault({
  collection,
  ...props
}: Omit<ComponentProps<typeof CollectionDefault>, "facets" | "results">) {
  return (
    <CollectionDefault
      {...{ collection, facets: [], ...props }}
      results={collection?.products?.filter(Boolean) ?? []}
    />
  )
}
